import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Package,
  CheckCircle2,
  ArrowRight,
  ImageOff,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import Navbar from "../components/nav/Navbar";
import ProductGridCard from "../components/ProductGridCard";
import { WebAdBanner, MobileAdToast } from "../components/AdBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Fallback featured product ──────────────────────────────────────────── */
const FALLBACK = {
  id: "prod_001",
  mongo_id: "prod_001",
  product_name: "AI Smart Fridge Vending Machine",
  headline: "Your Own 24/7 Passive Income Machine",
  tagline: "AI-powered. Cloud-managed. Completely hands-off.",
  sale_price: 1769.99,
  original_price: 2199.99,
  description_a:
    "The Vendbabee AI Smart Fridge is a next-generation grab-and-go vending solution powered by pure vision recognition technology. No coils. No jams. No missed sales. Customers open the door, take what they want, and the AI tallies their order automatically.",
  description_b:
    "An unbeatable investment for universities, hospitals, corporate campuses, gyms, and high-foot-traffic venues looking to monetize idle space.",
  description_c:
    "Every unit ships with a full 1-year warranty, CE certification, and direct technical support. Most orders deliver in 4–6 weeks.",
  great_points: [
    "AI Vision Recognition — no motors, no jams, no downtime.",
    "Cloud dashboard — manage stock, pricing & analytics remotely.",
    "Accepts cards, QR codes, and mobile wallets out of the box.",
  ],
  img_url: "https://images.unsplash.com/photo-1599762427591-4b754b538b1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  photo_urls: [
    "https://images.unsplash.com/photo-1599762427591-4b754b538b1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    "https://images.unsplash.com/photo-1720414574223-9f634e6035c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  ],
  warranty: "1 Year",
  place_of_origin: "Xi'an, Shaanxi, China",
};

/* ── Dummy products fallback ─────────────────────────────────────────────── */
const DUMMY = [
  { id: "d1", mongo_id: "d1", product_name: "Combo Pro 550", machine_type: "Combo Snack+Drink", sale_price: 3800, original_price: 4500, img_url: null },
  { id: "d2", mongo_id: "d2", product_name: "ChillDrop 300R", machine_type: "Refrigerated", sale_price: 2600, img_url: null },
  { id: "d3", mongo_id: "d3", product_name: "SnackStack 400", machine_type: "Standard Snack", sale_price: 1950, original_price: 2400, img_url: null },
  { id: "d4", mongo_id: "d4", product_name: "TouchMax 600", machine_type: "Combo Snack+Drink", sale_price: 6200, original_price: 7500, img_url: null },
  { id: "d5", mongo_id: "d5", product_name: "FrostSlim 200", machine_type: "Refrigerated", sale_price: 1800, img_url: null },
  { id: "d6", mongo_id: "d6", product_name: "MicroVend 150", machine_type: "Standard Snack", sale_price: 1100, original_price: 1350, img_url: null },
  { id: "d7", mongo_id: "d7", product_name: "ArcadePro Classic", machine_type: "Arcade Game", sale_price: 4200, img_url: null },
  { id: "d8", mongo_id: "d8", product_name: "CraneMax Deluxe", machine_type: "Crane/Claw Machine", sale_price: 2900, img_url: null },
];

/* ── Quote Dialog ────────────────────────────────────────────────────────── */
function QuoteDialog({ open, onOpenChange, formData, onChange, onSelect, onSubmit, submitting }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Get Business Pricing
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-5 mt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { id: "customer_name", label: "Full Name *", type: "text", required: true, capitalize: true },
              { id: "email", label: "Email *", type: "email", required: true },
              { id: "phone", label: "Phone *", type: "tel", required: true },
              { id: "company_name", label: "Company Name", type: "text" },
            ].map((f) => (
              <div key={f.id}>
                <Label htmlFor={f.id} className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {f.label}
                </Label>
                <Input id={f.id} name={f.id} type={f.type} value={formData[f.id]}
                  onChange={onChange} required={f.required} className="mt-1"
                  autoCapitalize={f.capitalize ? "words" : undefined}
                  style={f.capitalize ? { textTransform: "capitalize" } : undefined} />
              </div>
            ))}
            <div>
              <Label htmlFor="quantity" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Quantity *
              </Label>
              <Input id="quantity" name="quantity" type="number" min="1"
                value={formData.quantity} onChange={onChange} required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="location" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Delivery Location *
              </Label>
              <Input id="location" name="location" placeholder="City, State, Country"
                value={formData.location} onChange={onChange} required className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Budget Range
              </Label>
              <Select value={formData.budget_range} onValueChange={(v) => onSelect("budget_range", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="under_3000">Under $3,000</SelectItem>
                  <SelectItem value="3000_5000">$3,000 – $5,000</SelectItem>
                  <SelectItem value="5000_10000">$5,000 – $10,000</SelectItem>
                  <SelectItem value="10000_plus">$10,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="customization_details" className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Notes / Details
              </Label>
              <Textarea id="customization_details" name="customization_details"
                placeholder="Machine type, branding needs, location details..."
                value={formData.customization_details} onChange={onChange}
                className="mt-1 min-h-[80px]" />
            </div>
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
            {submitting ? "Submitting..." : "Submit Quote Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Photo Gallery ───────────────────────────────────────────────────────── */
function PhotoGallery({ photos }) {
  const [idx, setIdx] = useState(0);
  const list = photos?.length ? photos : [null];
  const total = list.length;

  // Reset to 0 when photos change
  useEffect(() => { setIdx(0); }, [photos]);
  const safeIdx = Math.min(idx, Math.max(0, total - 1));

  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative overflow-hidden"
        style={{ aspectRatio: "4/3", borderRadius: 20, background: "#f8f7ff", border: "1px solid hsl(var(--border))" }}
      >
        {list[safeIdx] ? (
          <img src={list[safeIdx]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-16 w-16" style={{ color: "#d0cff0" }} />
          </div>
        )}
        {total > 1 && (
          <>
            <button onClick={() => setIdx((i) => (i - 1 + total) % total)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full glass flex items-center justify-center">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => setIdx((i) => (i + 1) % total)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full glass flex items-center justify-center">
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {list.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} style={{
                  width: i === safeIdx ? 20 : 7, height: 7, borderRadius: 9999,
                  background: i === safeIdx ? "var(--vb-indigo)" : "rgba(0,0,0,0.18)",
                  transition: "all 0.2s ease", border: "none", cursor: "pointer", padding: 0,
                }} />
              ))}
            </div>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {list.map((img, i) => (
            <button key={i} onClick={() => setIdx(i)} className="flex-shrink-0 overflow-hidden"
              style={{ width: 64, height: 52, borderRadius: 10, border: "none", cursor: "pointer",
                outline: `2px solid ${i === safeIdx ? "var(--vb-indigo)" : "hsl(var(--border))"}`,
                opacity: i === safeIdx ? 1 : 0.55 }}>
              {img
                ? <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <div style={{ width: "100%", height: "100%", background: "#f0f0f0" }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Buy Now Button ──────────────────────────────────────────────────────── */
function BuyNowButton({ product, fullWidth = false }) {
  const [loading, setLoading] = useState(false);
  const fmtPrice = (v) => v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handle = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout/buy-now`, {
        product_id: product.mongo_id || product.id,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Checkout unavailable. Please try again or contact us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handle}
      disabled={loading}
      className="btn-primary"
      style={{
        height: 56, fontSize: 15, borderRadius: 14,
        width: fullWidth ? "100%" : undefined,
        justifyContent: "center",
        opacity: loading ? 0.7 : 1,
        cursor: loading ? "not-allowed" : "pointer",
      }}
    >
      <CreditCard className="h-4 w-4" />
      {loading ? "Redirecting..." : `Buy Now — $${fmtPrice(product?.sale_price)}`}
    </button>
  );
}

/* ── Masonry Grid ────────────────────────────────────────────────────────── */
function MasonryGrid({ products }) {
  const navigate = useNavigate();
  if (!products.length) return null;

  const masonry = products.slice(0, 5);
  const grid    = products.slice(5);
  const fmtPrice = (v) => v?.toLocaleString("en-US", { minimumFractionDigits: 2 });

  /* Individual card — heights balanced (tall=420, standard=280) */
  const MasonryCard = ({ product, tall = false }) => {
    const id = product.mongo_id || product.id;
    const hasDiscount = product.original_price && product.sale_price && product.sale_price < product.original_price;
    const discountPct = hasDiscount
      ? Math.round(((product.original_price - product.sale_price) / product.original_price) * 100)
      : null;

    return (
      <div
        onClick={() => navigate(`/product/${id}`)}
        className="product-card relative overflow-hidden"
        style={{
          borderRadius: 20,
          height: tall ? 420 : 280,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Image */}
        <div className="product-card__img flex-1" style={{ borderRadius: "20px 20px 0 0", minHeight: 0 }}>
          {product.img_url ? (
            <img src={product.img_url} alt={product.product_name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #f8f7ff, #ecebff)" }}>
              <ImageOff className="h-10 w-10" style={{ color: "#c0bff0" }} />
            </div>
          )}
          {discountPct && (
            <div className="chip chip-discount absolute top-3 left-3 z-10"
              style={{ borderRadius: 8, padding: "3px 10px" }}>
              -{discountPct}%
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-white" style={{ borderRadius: "0 0 20px 20px", flexShrink: 0 }}>
          {product.machine_type && (
            <span className="chip chip-primary text-[10px] mb-2 inline-flex">{product.machine_type}</span>
          )}
          <p className="font-heading font-semibold text-sm text-foreground leading-tight line-clamp-1 mb-2"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {product.product_name}
          </p>
          <div className="flex items-center justify-between gap-3">
            {product.sale_price ? (
              <span className="font-heading font-bold text-base"
                style={{ color: "var(--vb-indigo)", fontFamily: "'Oswald', sans-serif" }}>
                ${fmtPrice(product.sale_price)}
              </span>
            ) : (
              <span className="font-body text-xs text-muted-foreground italic">Quote only</span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/product/${id}`); }}
              className="chip chip-primary text-[10px] flex-shrink-0"
              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              View <ArrowRight className="h-2.5 w-2.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Masonry: 3-column, item 0 spans 2 rows on the left */}
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        {masonry[0] && (
          <div style={{ gridRow: "span 2" }}>
            <MasonryCard product={masonry[0]} tall />
          </div>
        )}
        {masonry[1] && <MasonryCard product={masonry[1]} />}
        {masonry[2] && <MasonryCard product={masonry[2]} />}
        {masonry[3] && <MasonryCard product={masonry[3]} />}
        {masonry[4] && <MasonryCard product={masonry[4]} />}
      </div>

      {/* 2×2 grid for remaining products */}
      {grid.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          {grid.map((p) => (
            <MasonryCard key={p.mongo_id || p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   OFFERING PAGE
   Hero funnel (Section 1) + Masonry grid (Section 2) + CTA band (Section 3)
══════════════════════════════════════════════════════════════════════════ */
export default function Offering() {
  const navigate = useNavigate();
  const { instanceId } = useParams();

  const [featuredProduct, setFeaturedProduct] = useState(FALLBACK);
  const [featuredLoading, setFeaturedLoading] = useState(!!instanceId);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "", email: "", phone: "", company_name: "",
    location: "", quantity: 1, customization_details: "", budget_range: "",
    payment_options: [], additional_features: [], machine_type: "",
  });

  /* Fetch featured product by instance_id */
  useEffect(() => {
    if (!instanceId) { setFeaturedLoading(false); return; }
    setFeaturedLoading(true);
    axios.get(`${API}/products/instance/${instanceId}`)
      .then((r) => { if (r.data) setFeaturedProduct(r.data); })
      .catch(() => {})
      .finally(() => setFeaturedLoading(false));
  }, [instanceId]);

  /* Fetch marketplace products */
  useEffect(() => {
    axios.get(`${API}/products`, { params: { status: "Active" } })
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : [];
        setProducts(all.length ? all : DUMMY);
      })
      .catch(() => setProducts(DUMMY))
      .finally(() => setProductsLoading(false));
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };
  const onSelect = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotes`, formData);
      if (res.data.success) {
        toast.success("Quote submitted! We'll be in touch within 24 hours.");
        setIsQuoteOpen(false);
        setFormData({
          customer_name: "", email: "", phone: "", company_name: "",
          location: "", quantity: 1, customization_details: "", budget_range: "",
          payment_options: [], additional_features: [], machine_type: "",
        });
      }
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const p = featuredProduct;
  const galleryPhotos = p?.photo_urls?.length ? p.photo_urls : p?.img_url ? [p.img_url] : [];
  const hasDiscount   = p?.original_price && p?.sale_price && p.sale_price < p.original_price;
  const discountPct   = hasDiscount ? Math.round((1 - p.sale_price / p.original_price) * 100) : null;
  const fmtPrice      = (v) => v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const greatPoints = p?.great_points?.length
    ? p.great_points
    : [
        "Industrial-grade build — CE certified, 1-year warranty included.",
        "Cloud dashboard — manage inventory and sales from anywhere.",
        "Accepts cards, QR codes, and mobile wallets out of the box.",
      ];

  const featuredId   = p?.mongo_id || p?.id;
  const restProducts = products.filter((x) => (x.mongo_id || x.id) !== featuredId);

  return (
    <div className="min-h-screen bg-white">
      <Navbar onCtaClick={() => setIsQuoteOpen(true)} ctaLabel="Get Business Pricing" />

      <QuoteDialog
        open={isQuoteOpen}
        onOpenChange={setIsQuoteOpen}
        formData={formData}
        onChange={onChange}
        onSelect={onSelect}
        onSubmit={handleSubmit}
        submitting={isSubmitting}
      />

      {/* ════════════════════════════════════════════════════════
          SECTION 1 — HERO FUNNEL
          Featured product: photo left, pricing + CTA right
      ════════════════════════════════════════════════════════ */}
      <section style={{ paddingTop: 36 + 68, background: "#ffffff" }}>
        <div className="container-lg py-16 lg:py-20">

          {/* Eyebrow + headline + tagline */}
          <div className="text-center mb-12">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
              Vendbabee — Smart Vending
            </p>
            {featuredLoading ? (
              <div className="skeleton h-12 w-2/3 mx-auto mb-3" style={{ borderRadius: 8 }} />
            ) : (
              <>
                <h1
                  className="font-heading text-foreground leading-tight mb-4"
                  style={{
                    fontFamily: "'Oswald', sans-serif",
                    fontWeight: 600,
                    fontSize: "clamp(2.4rem, 5vw, 4.2rem)",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {p?.headline || p?.product_name || "Your 24/7 Passive Income Machine"}
                </h1>
                <p className="font-body text-muted-foreground text-lg max-w-xl mx-auto">
                  {p?.tagline || "AI-powered. Cloud-managed. Completely hands-off."}
                </p>
              </>
            )}
          </div>

          {/* Product layout: gallery left, info right */}
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-start">

            {/* Gallery — sticky on desktop */}
            <div className="lg:sticky lg:top-28">
              <PhotoGallery photos={galleryPhotos} />
            </div>

            {/* Pricing + CTA + details */}
            <div className="flex flex-col gap-6">

              {/* Pricing block */}
              <div style={{ background: "#f8f7ff", borderRadius: 16, border: "1px solid hsl(var(--border))", padding: "20px 24px" }}>
                {hasDiscount ? (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Sale Price</p>
                    <div className="flex items-end gap-3 flex-wrap">
                      <span className="font-heading font-bold"
                        style={{ fontFamily: "'Oswald', sans-serif", fontSize: "2.6rem", color: "var(--vb-indigo)" }}>
                        ${fmtPrice(p.sale_price)}
                      </span>
                      <div className="flex items-center gap-2 pb-1.5">
                        <span className="font-body text-lg text-muted-foreground line-through">
                          ${fmtPrice(p.original_price)}
                        </span>
                        <span className="chip chip-discount">-{discountPct}%</span>
                      </div>
                    </div>
                    <span className="chip chip-savings mt-2 inline-flex">
                      You save ${fmtPrice(p.original_price - p.sale_price)}
                    </span>
                  </div>
                ) : p?.sale_price ? (
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Price</p>
                    <span className="font-heading font-bold"
                      style={{ fontFamily: "'Oswald', sans-serif", fontSize: "2.6rem", color: "var(--vb-indigo)" }}>
                      ${fmtPrice(p.sale_price)}
                    </span>
                  </div>
                ) : (
                  <p className="font-body text-base font-semibold text-foreground">Custom pricing — get a quote</p>
                )}
                {p?.moq && (
                  <p className="font-body text-xs text-muted-foreground mt-3">Min. order: {p.moq}</p>
                )}
              </div>

              {/* CTA buttons */}
              <div className="flex flex-col gap-3">
                {p?.sale_price && <BuyNowButton product={p} fullWidth />}
                <button
                  onClick={() => setIsQuoteOpen(true)}
                  className="btn-secondary w-full justify-center"
                  style={{ height: 48 }}
                >
                  Get a Custom Quote →
                </button>
              </div>

              {/* Payment badges */}
              <div className="flex flex-wrap gap-1.5">
                {["Visa", "Mastercard", "Amex", "Discover", "Apple Pay", "Google Pay"].map((n) => (
                  <span key={n} className="payment-badge">{n}</span>
                ))}
              </div>

              {/* Great points */}
              <div className="flex flex-col gap-3 pt-2 border-t border-border">
                {greatPoints.map((pt, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "var(--vb-indigo)" }} />
                    <p className="font-body text-sm text-foreground leading-relaxed">{pt}</p>
                  </div>
                ))}
              </div>

              {/* Description A */}
              {p?.description_a && (
                <p className="font-body text-sm text-muted-foreground leading-relaxed">
                  {p.description_a}
                </p>
              )}

              {/* Description B — italic pull quote */}
              {p?.description_b && (
                <p className="font-body text-sm text-muted-foreground leading-relaxed italic border-l-2 pl-4"
                  style={{ borderColor: "var(--vb-indigo)" }}>
                  {p.description_b}
                </p>
              )}

              {/* Bottom CTA */}
              {p?.sale_price && (
                <div className="pt-2">
                  <BuyNowButton product={p} fullWidth />
                  <p className="font-body text-xs text-muted-foreground text-center mt-2">
                    Secure Stripe checkout · Shipping included at checkout
                  </p>
                </div>
              )}

              {/* Description C */}
              {p?.description_c && (
                <p className="font-body text-xs text-muted-foreground leading-relaxed border-t border-border pt-4">
                  {p.description_c}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 2 — MASONRY PRODUCT GRID
      ════════════════════════════════════════════════════════ */}
      <section style={{ background: "#ffffff", padding: "80px 0" }}>
        <div className="container-lg">
          <MobileAdToast />

          {/* Header */}
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Also Available
              </p>
              <h2 className="font-heading text-foreground"
                style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "clamp(1.6rem, 2.5vw, 2.4rem)" }}>
                Browse More Machines
              </h2>
            </div>
            <button onClick={() => navigate("/shop")} className="btn-ghost hidden sm:flex">
              View full catalogue →
            </button>
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-3 gap-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="skeleton" style={{ height: i === 0 ? 420 : 280, borderRadius: 20 }} />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop: masonry + right ad rail — ad does NOT push product grid */}
              <div className="hidden md:block relative">
                <MasonryGrid products={restProducts.slice(0, 9)} />
                {/* Ad rail: absolutely positioned outside the grid flow */}
                <div
                  className="hidden xl:block absolute"
                  style={{
                    top: 0,
                    right: -270,  /* sits outside the container to the right */
                    width: 250,
                  }}
                >
                  <WebAdBanner slot={1} />
                </div>
              </div>

              {/* Mobile: horizontal swipe row */}
              <div className="md:hidden swipe-row">
                {(restProducts.length ? restProducts : DUMMY).map((prod) => (
                  <ProductGridCard key={prod.mongo_id || prod.id} product={prod} mobile />
                ))}
              </div>
            </>
          )}

          {/* Mobile view all */}
          <div className="md:hidden mt-6 text-center">
            <button onClick={() => navigate("/shop")} className="btn-secondary">
              View full catalogue →
            </button>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          SECTION 3 — CTA BAND
      ════════════════════════════════════════════════════════ */}
      <section style={{ background: "var(--grad-primary)", padding: "80px 0" }}>
        <div className="container-lg text-center">
          <p className="font-mono text-[10px] uppercase tracking-widest mb-4"
            style={{ color: "rgba(255,255,255,0.6)" }}>
            Start Your Vending Business
          </p>
          <h2 className="font-heading text-white mb-6"
            style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: "clamp(2rem, 4vw, 3.2rem)" }}>
            Ready to earn passive income?
          </h2>
          <p className="font-body text-base mb-10 max-w-xl mx-auto"
            style={{ color: "rgba(255,255,255,0.75)" }}>
            Join hundreds of operators running smart vending machines across the US, Canada, and beyond.
            Start with one machine and scale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {p?.sale_price && <BuyNowButton product={p} />}
            <button
              onClick={() => setIsQuoteOpen(true)}
              className="font-body font-semibold text-sm px-8 py-4 rounded-2xl transition-all"
              style={{
                background: "transparent", color: "#ffffff",
                border: "2px solid rgba(255,255,255,0.5)", cursor: "pointer",
              }}
            >
              Get a Business Quote
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "#0a0018", color: "#f8f7ff", padding: "48px 0" }}>
        <div className="container-lg flex items-center justify-between flex-wrap gap-6">
          <div>
            <p className="font-heading text-base font-semibold mb-1"
              style={{ fontFamily: "'Oswald', sans-serif" }}>Vendbabee</p>
            <p className="font-body text-xs" style={{ color: "rgba(248,247,255,0.4)" }}>
              Smart vending machines for the US, Canada, EU, and beyond.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={() => setIsQuoteOpen(true)}
              className="font-body text-xs cursor-pointer"
              style={{ background: "none", border: "none", color: "rgba(248,247,255,0.5)",
                textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}
            >
              Get Business Pricing
            </button>
            <p className="font-body text-xs" style={{ color: "rgba(248,247,255,0.25)" }}>
              © {new Date().getFullYear()} Vendbabee. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
